import { defineStore } from "pinia";
import { handleError, handleApiResponse } from "@/utils/common.js";
import { authData, multipartData } from "@/api/apiService";
import {
  ROLE_INVITATIONS,
  ROLE_INVITATIONS_RESPOND,
  GET_AGENT_DASHBOARD,
  REGISTER_DISPUTE,
  AGENT_REGISTER_INCAPACITATION,
  AGENT_REPUDIATE,
  AGENT_RELEASE_ITEM,
  GET_AGENT_RELEASE_ITEM,
  AGENT_RELEASE_STORY,
  GET_AGENT_RELEASE_STORY,
  AGENT_RELEASE_MESSAGE,
  GET_AGENT_RELEASE_MESSAGE,
  AGENT_GET_DOCUMENT,
  AGENT_DOWNLOAD_DOCUMENT,
  AGENT_DEATH_CERTIFICATE_UPLOAD,
  GET_AGENT_DEATH_CERTIFICATE,
  AGENT_DIGITAL_VAULT,
  AGENT_PROFILE,
  AGENT_IMAGE,
} from "@/api/apiRoute";
import { errorMessage } from "@/utils/errorMessages";

export const useRolesStore = defineStore("rolesStore", {
  state: () => ({
    errorDisplayDuration: 4000,
    errorMsg: "",
    loading: false,
    declineLoading: false,
    roleInvitations: [],
    hint: null,
    passphrase: null,
    agentDashboardData: null,
    getDocumentList: [],
    showDisputeDialog: false,
    agentDigitalVaultData: null,
    agentProfileImage: null, // Kept for backward compatibility
    agentProfileImageCache: new Map(), // Cache images by user ID
    agentProfileData: null,
    AgentUserName: null,
    userId: null,
    agentDeathCertificate: null,
  }),
  getters: {
    requestCount: (state) =>
      state.roleInvitations?.filter((req) => req.status === "proposed")
        .length || 0,
  },
  actions: {
    clearErrorMessage() {
      this.errorMsg = "";
    },
    setErrorMessage(error) {
      this.errorMsg = errorMessage[error] || error || "An error occurred";
    },
    showError(error) {
      this.setErrorMessage(error?.response?.data?.error);
      setTimeout(() => {
        this.clearErrorMessage();
      }, this.errorDisplayDuration);
      handleError(error);
      throw error;
    },
    async getRequests() {
      this.loading = true;
      try {
        const result = await authData.get(ROLE_INVITATIONS);
        this.roleInvitations = result.data.data || [];
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async respondInvitation(payload) {
      if (payload.status === "accepted") {
        this.loading = true;
      } else if (payload.status === "declined") {
        this.declineLoading = true;
      }
      try {
        const result = await authData.post(ROLE_INVITATIONS_RESPOND, payload);
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        if (payload.status === "accepted") {
          this.loading = false;
        } else if (payload.status === "declined") {
          this.declineLoading = false;
        }
      }
    },
    async getAgentDashboard(agentID) {
      try {
        const response = await authData.get(GET_AGENT_DASHBOARD(agentID));
        this.agentDashboardData = response.data.data || null;
        return handleApiResponse(response);
      } catch (error) {
        return this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async registerDispute(payload) {
      this.loading = true;
      try {
        const result = await authData.post(REGISTER_DISPUTE, payload);
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async registerRepudiate(id) {
      this.loading = true;
      try {
        const result = await authData.post(AGENT_REPUDIATE(id));
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async agentRegisterIncap(id, payload) {
      this.loading = true;
      try {
        const result = await authData.post(
          AGENT_REGISTER_INCAPACITATION(id),
          payload
        );
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async agentReleaseItem(id) {
      this.loading = true;
      try {
        const result = await authData.post(AGENT_RELEASE_ITEM(id));
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async getAgentReleaseItem(id) {
      this.loading = true;
      try {
        const result = await authData.get(GET_AGENT_RELEASE_ITEM(id));
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async agentStory(id) {
      this.loading = true;
      try {
        const result = await authData.post(AGENT_RELEASE_STORY(id));
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async getAgentStory(id) {
      this.loading = true;
      try {
        const result = await authData.get(GET_AGENT_RELEASE_STORY(id));
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async agentMessage(id) {
      this.loading = true;
      try {
        const result = await authData.post(AGENT_RELEASE_MESSAGE(id));
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async getAgentMessage(id) {
      this.loading = true;
      try {
        const result = await authData.get(GET_AGENT_RELEASE_MESSAGE(id));
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async getAgentDocument(id) {
      this.loading = true;
      try {
        const result = await authData.get(AGENT_GET_DOCUMENT(id));
        this.getDocumentList = result.data.data || [];
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async getAgentDocumentDownload(id, documentSlug) {
      this.loading = true;
      try {
        const result = await authData.get(
          AGENT_DOWNLOAD_DOCUMENT(id, documentSlug),
          { responseType: "blob" }
        );
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async agentDeathCertificateUpload(id, payload) {
      this.loading = true;
      try {
        const result = await multipartData.post(
          AGENT_DEATH_CERTIFICATE_UPLOAD(id),
          payload
        );
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async getAgentDeathCertificate(id) {
      this.loading = true;
      try {
        const result = await authData.get(GET_AGENT_DEATH_CERTIFICATE(id), {
          responseType: "arraybuffer",
        });
        const base64 = btoa(
          new Uint8Array(result.data).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            ""
          )
        );

        const imageUrl = `data:image/png;base64,${base64}`;
        this.agentDeathCertificate = imageUrl;
        return imageUrl;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async getAgentDigitalVault(id) {
      this.loading = true;
      try {
        const result = await authData.get(AGENT_DIGITAL_VAULT(id));
        this.agentDigitalVaultData = result.data.data || [];
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async getAgentProfile(id) {
      this.loading = true;
      try {
        const result = await authData.get(AGENT_PROFILE(id));
        this.agentProfileData = result.data.data || [];
        return result;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
    async getAgentProfileImage(id) {
      // Return cached image if available
      if (this.agentProfileImageCache.has(id)) {
        return this.agentProfileImageCache.get(id);
      }

      this.loading = true;
      try {
        const result = await authData.get(AGENT_IMAGE(id), {
          responseType: "arraybuffer",
        });
        const base64 = btoa(
          new Uint8Array(result.data).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            ""
          )
        );

        const imageUrl = `data:image/png;base64,${base64}`;
        // Store in cache and legacy state
        this.agentProfileImageCache.set(id, imageUrl);
        this.agentProfileImage = imageUrl; // Keep for backward compatibility
        return imageUrl;
      } catch (error) {
        this.showError(error);
        return null;
      } finally {
        this.loading = false;
      }
    },
  },
});
